import React from "react";
import { observer } from "mobx-react";
import { SatelliteMachineUpdateStore } from "../../store";
import { SatelliteMachineUpdateForm } from "./SatelliteMachineUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";

export type SatelliteMachineUpdateProps = {
  satelliteMachineUpdateStore: SatelliteMachineUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const SatelliteMachineUpdate: React.FC<SatelliteMachineUpdateProps> =
  observer(
    ({ satelliteMachineUpdateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={satelliteMachineUpdateStore.isSaving} />
          <OutdatedAfterSaveNotice
            open={satelliteMachineUpdateStore.savedButOutdated}
            onReload={() =>
              satelliteMachineUpdateStore.reloadSatelliteMachine()
            }
            reloadDisabled={satelliteMachineUpdateStore.isProcessing}
          />
          <SatelliteMachineUpdateForm
            satelliteMachineUpdateStore={satelliteMachineUpdateStore}
            nameError={satelliteMachineUpdateStore.errors.name?.message}
            onNameChange={(value: string) => {
              satelliteMachineUpdateStore.setNameValue(value);
            }}
            name={satelliteMachineUpdateStore.entityValues.name}
            machineCodeError={
              satelliteMachineUpdateStore.errors.machineCode?.message
            }
            onMachineCodeChange={(value: string) => {
              satelliteMachineUpdateStore.setMachineCodeValue(value);
            }}
            machineCode={satelliteMachineUpdateStore.entityValues.machineCode}
            satelliteTypeError={
              satelliteMachineUpdateStore.errors.satelliteType?.message
            }
            onSatelliteTypeChange={(value: string) => {
              satelliteMachineUpdateStore.setSatelliteTypeValue(value);
            }}
            satelliteType={
              satelliteMachineUpdateStore.entityValues.satelliteType
            }
            regionError={satelliteMachineUpdateStore.errors.region?.message}
            onRegionChange={(value: string) => {
              satelliteMachineUpdateStore.setRegionValue(value);
            }}
            region={satelliteMachineUpdateStore.entityValues.region}
            certificateFingerprintError={
              satelliteMachineUpdateStore.errors.certificateFingerprint?.message
            }
            onCertificateFingerprintChange={(value: string) => {
              satelliteMachineUpdateStore.setCertificateFingerprintValue(value);
            }}
            certificateFingerprint={
              satelliteMachineUpdateStore.entityValues.certificateFingerprint
            }
            certificateExpiresAtError={
              satelliteMachineUpdateStore.errors.certificateExpiresAt?.message
            }
            onCertificateExpiresAtChange={(value) => {
              satelliteMachineUpdateStore.setCertificateExpiresAtValue(value);
            }}
            certificateExpiresAt={
              satelliteMachineUpdateStore.entityValues.certificateExpiresAt
            }
            authTokenHashError={
              satelliteMachineUpdateStore.errors.authTokenHash?.message
            }
            onAuthTokenHashChange={(value: string) => {
              satelliteMachineUpdateStore.setAuthTokenHashValue(value);
            }}
            authTokenHash={
              satelliteMachineUpdateStore.entityValues.authTokenHash
            }
            authTokenLastFourError={
              satelliteMachineUpdateStore.errors.authTokenLastFour?.message
            }
            onAuthTokenLastFourChange={(value: string) => {
              satelliteMachineUpdateStore.setAuthTokenLastFourValue(value);
            }}
            authTokenLastFour={
              satelliteMachineUpdateStore.entityValues.authTokenLastFour
            }
            authTokenCreatedAtError={
              satelliteMachineUpdateStore.errors.authTokenCreatedAt?.message
            }
            onAuthTokenCreatedAtChange={(value) => {
              satelliteMachineUpdateStore.setAuthTokenCreatedAtValue(value);
            }}
            authTokenCreatedAt={
              satelliteMachineUpdateStore.entityValues.authTokenCreatedAt
            }
            authTokenRotatedAtError={
              satelliteMachineUpdateStore.errors.authTokenRotatedAt?.message
            }
            onAuthTokenRotatedAtChange={(value) => {
              satelliteMachineUpdateStore.setAuthTokenRotatedAtValue(value);
            }}
            authTokenRotatedAt={
              satelliteMachineUpdateStore.entityValues.authTokenRotatedAt
            }
            statusError={satelliteMachineUpdateStore.errors.status?.message}
            onStatusChange={(value: string) => {
              satelliteMachineUpdateStore.setStatusValue(value);
            }}
            status={satelliteMachineUpdateStore.entityValues.status}
            authorizedAtError={
              satelliteMachineUpdateStore.errors.authorizedAt?.message
            }
            onAuthorizedAtChange={(value) => {
              satelliteMachineUpdateStore.setAuthorizedAtValue(value);
            }}
            authorizedAt={satelliteMachineUpdateStore.entityValues.authorizedAt}
            authorizedByError={
              satelliteMachineUpdateStore.errors.authorizedBy?.message
            }
            onAuthorizedByChange={(value) => {
              satelliteMachineUpdateStore.setAuthorizedByValue(value);
            }}
            authorizedBySelectedValue={
              satelliteMachineUpdateStore.entityValues.showAuthorizedBy
            }
            onAuthorizedBySelected={(value) => {
              satelliteMachineUpdateStore.setSelectedAuthorizedBy(value);
            }}
            authorizedByOptions={satelliteMachineUpdateStore.getAuthorizedByOptions()}
            suspendedAtError={
              satelliteMachineUpdateStore.errors.suspendedAt?.message
            }
            onSuspendedAtChange={(value) => {
              satelliteMachineUpdateStore.setSuspendedAtValue(value);
            }}
            suspendedAt={satelliteMachineUpdateStore.entityValues.suspendedAt}
            suspendedByError={
              satelliteMachineUpdateStore.errors.suspendedBy?.message
            }
            onSuspendedByChange={(value) => {
              satelliteMachineUpdateStore.setSuspendedByValue(value);
            }}
            suspendedBySelectedValue={
              satelliteMachineUpdateStore.entityValues.showSuspendedBy
            }
            onSuspendedBySelected={(value) => {
              satelliteMachineUpdateStore.setSelectedSuspendedBy(value);
            }}
            suspendedByOptions={satelliteMachineUpdateStore.getSuspendedByOptions()}
            suspensionReasonError={
              satelliteMachineUpdateStore.errors.suspensionReason?.message
            }
            onSuspensionReasonChange={(value: string) => {
              satelliteMachineUpdateStore.setSuspensionReasonValue(value);
            }}
            suspensionReason={
              satelliteMachineUpdateStore.entityValues.suspensionReason
            }
            enabledError={satelliteMachineUpdateStore.errors.enabled?.message}
            onEnabledChange={(value: boolean) => {
              satelliteMachineUpdateStore.setEnabledValue(value);
            }}
            enabled={satelliteMachineUpdateStore.entityValues.enabled}
            allowedTenantIdsError={
              satelliteMachineUpdateStore.errors.allowedTenantIds?.message
            }
            onAllowedTenantIdsChange={(value: string) => {
              satelliteMachineUpdateStore.setAllowedTenantIdsValue(value);
            }}
            allowedTenantIds={
              satelliteMachineUpdateStore.entityValues.allowedTenantIds
            }
            proxyEnabledError={
              satelliteMachineUpdateStore.errors.proxyEnabled?.message
            }
            onProxyEnabledChange={(value: boolean) => {
              satelliteMachineUpdateStore.setProxyEnabledValue(value);
            }}
            proxyEnabled={satelliteMachineUpdateStore.entityValues.proxyEnabled}
            proxyPortError={
              satelliteMachineUpdateStore.errors.proxyPort?.message
            }
            onProxyPortChange={(value: number | undefined) => {
              satelliteMachineUpdateStore.setProxyPortValue(value);
            }}
            proxyPort={satelliteMachineUpdateStore.entityValues.proxyPort}
            proxyProtocolsError={
              satelliteMachineUpdateStore.errors.proxyProtocols?.message
            }
            onProxyProtocolsChange={(value: string) => {
              satelliteMachineUpdateStore.setProxyProtocolsValue(value);
            }}
            proxyProtocols={
              satelliteMachineUpdateStore.entityValues.proxyProtocols
            }
            botEnabledError={
              satelliteMachineUpdateStore.errors.botEnabled?.message
            }
            onBotEnabledChange={(value: boolean) => {
              satelliteMachineUpdateStore.setBotEnabledValue(value);
            }}
            botEnabled={satelliteMachineUpdateStore.entityValues.botEnabled}
            botMaxConcurrentError={
              satelliteMachineUpdateStore.errors.botMaxConcurrent?.message
            }
            onBotMaxConcurrentChange={(value: number | undefined) => {
              satelliteMachineUpdateStore.setBotMaxConcurrentValue(value);
            }}
            botMaxConcurrent={
              satelliteMachineUpdateStore.entityValues.botMaxConcurrent
            }
            workerEnabledError={
              satelliteMachineUpdateStore.errors.workerEnabled?.message
            }
            onWorkerEnabledChange={(value: boolean) => {
              satelliteMachineUpdateStore.setWorkerEnabledValue(value);
            }}
            workerEnabled={
              satelliteMachineUpdateStore.entityValues.workerEnabled
            }
            publicIpError={satelliteMachineUpdateStore.errors.publicIp?.message}
            onPublicIpChange={(value: string) => {
              satelliteMachineUpdateStore.setPublicIpValue(value);
            }}
            publicIp={satelliteMachineUpdateStore.entityValues.publicIp}
            locationError={satelliteMachineUpdateStore.errors.location?.message}
            onLocationChange={(value: string) => {
              satelliteMachineUpdateStore.setLocationValue(value);
            }}
            location={satelliteMachineUpdateStore.entityValues.location}
            countryCodeError={
              satelliteMachineUpdateStore.errors.countryCode?.message
            }
            onCountryCodeChange={(value: string) => {
              satelliteMachineUpdateStore.setCountryCodeValue(value);
            }}
            countryCode={satelliteMachineUpdateStore.entityValues.countryCode}
            lastHeartbeatAtError={
              satelliteMachineUpdateStore.errors.lastHeartbeatAt?.message
            }
            onLastHeartbeatAtChange={(value) => {
              satelliteMachineUpdateStore.setLastHeartbeatAtValue(value);
            }}
            lastHeartbeatAt={
              satelliteMachineUpdateStore.entityValues.lastHeartbeatAt
            }
            lastHeartbeatMetricsError={
              satelliteMachineUpdateStore.errors.lastHeartbeatMetrics?.message
            }
            onLastHeartbeatMetricsChange={(value: string) => {
              satelliteMachineUpdateStore.setLastHeartbeatMetricsValue(value);
            }}
            lastHeartbeatMetrics={
              satelliteMachineUpdateStore.entityValues.lastHeartbeatMetrics
            }
            connectedAtError={
              satelliteMachineUpdateStore.errors.connectedAt?.message
            }
            onConnectedAtChange={(value) => {
              satelliteMachineUpdateStore.setConnectedAtValue(value);
            }}
            connectedAt={satelliteMachineUpdateStore.entityValues.connectedAt}
            disconnectedAtError={
              satelliteMachineUpdateStore.errors.disconnectedAt?.message
            }
            onDisconnectedAtChange={(value) => {
              satelliteMachineUpdateStore.setDisconnectedAtValue(value);
            }}
            disconnectedAt={
              satelliteMachineUpdateStore.entityValues.disconnectedAt
            }
            tunnelConnectedAtError={
              satelliteMachineUpdateStore.errors.tunnelConnectedAt?.message
            }
            onTunnelConnectedAtChange={(value) => {
              satelliteMachineUpdateStore.setTunnelConnectedAtValue(value);
            }}
            tunnelConnectedAt={
              satelliteMachineUpdateStore.entityValues.tunnelConnectedAt
            }
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={satelliteMachineUpdateStore.saveDisabled}
            onSave={() => satelliteMachineUpdateStore.saveSatelliteMachine()}
            onCancel={() =>
              satelliteMachineUpdateStore.cancelSatelliteMachine()
            }
          />
          <ErrorDialog
            open={satelliteMachineUpdateStore.errorDialogOpened}
            title={satelliteMachineUpdateStore.errorTitle}
            description={satelliteMachineUpdateStore.errorDescription}
            onClose={() => satelliteMachineUpdateStore.closeErrorDialog()}
          />
          <ConfirmCancelDialog
            open={satelliteMachineUpdateStore.confirmDialogOpened}
            title={satelliteMachineUpdateStore.confirmDialogTitle}
            description={satelliteMachineUpdateStore.confirmDialogDescription}
            onConfirm={() =>
              satelliteMachineUpdateStore.executeConfirmedAction()
            }
            onCancel={() => satelliteMachineUpdateStore.closeConfirmDialog()}
          />
        </>
      );
    }
  );
